import { useTranslation } from "react-i18next";
import { useToast } from "../../../mantine/notifications/useToast";
import { LoadOrError } from "../../LoadOrError/LoadOrError";
import { useInvoiceNumberRangeMutations } from "./hooks/useInvoiceNumberRangeMutations";
import { useInvoiceNumberRanges } from "./hooks/useInvoiceNumberRanges";
import { usePPAInvoiceSuppliers } from "./hooks/usePPAInvoiceSuppliers";
import type { PPAInvoiceNumberRange } from "./PPAInvoiceNumberRangesPage.types";
import { PPAInvoiceNumberRangesTable } from "./PPAInvoiceNumberRangesTable/PPAInvoiceNumberRangesTable";

function PPAInvoiceNumberRangesPage() {
  const { t } = useTranslation();
  const {
    data: invoiceNumberRanges,
    isLoading: isInvoiceNumberRangesLoading,
    error: invoiceNumberRangesError
  } = useInvoiceNumberRanges();
  const { showToast, showErrorToast } = useToast();
  const invoiceNumberRangeMutations = useInvoiceNumberRangeMutations();
  const {
    data: suppliers,
    isLoading: isSuppliersLoading,
    error: suppliersError
  } = usePPAInvoiceSuppliers();

  async function handleSaveInvoiceNumberRange(
    invoiceNumberRangeFormValues: Omit<
      PPAInvoiceNumberRange,
      "id" | "counter_reset_interval"
    >,
    invoiceNumberRangeId?: string
  ) {
    const isLocal = typeof invoiceNumberRangeId === "undefined";

    if (isLocal) {
      const response =
        await invoiceNumberRangeMutations.createMutation.mutateAsync({
          newRange: invoiceNumberRangeFormValues
        });

      if (response.status === 201) {
        showToast({
          type: "success",
          message: "Ihr Rechnungsnummernkreis wurde erfolgreich gespeichert."
        });

        return Promise.resolve(response);
      }

      return Promise.reject(response);
    }

    console.error(
      "Range already exists. Editing existing ranges is not supported."
    );

    throw new Error(t("errors.ServerError"));
  }

  async function handleDeleteInvoiceNumberRange(invoiceNumberRangeId: string) {
    try {
      await invoiceNumberRangeMutations.deleteMutation.mutateAsync(
        invoiceNumberRangeId
      );
    } catch (error) {
      showErrorToast(error);
      return Promise.resolve();
    }

    showToast({
      type: "success",
      message: "Ihr Rechnungsnummernkreis wurde erfolgreich gelöscht."
    });

    return Promise.resolve();
  }

  return (
    <div className="PPAInvoiceNumberRangesPage">
      <LoadOrError
        error={invoiceNumberRangesError || suppliersError}
        loading={isInvoiceNumberRangesLoading || isSuppliersLoading}
        loadingMessage="Nummernkreise werden geladen ..."
      >
        {invoiceNumberRanges && suppliers && (
          <PPAInvoiceNumberRangesTable
            invoiceNumberRanges={invoiceNumberRanges}
            suppliers={suppliers}
            onDelete={handleDeleteInvoiceNumberRange}
            onSubmit={handleSaveInvoiceNumberRange}
          />
        )}
      </LoadOrError>
    </div>
  );
}

export { PPAInvoiceNumberRangesPage };
